/**
 * Shared price-point engine for the raw MDF cut-to-size builders
 * (Standard MDF, MR MDF, FR MDF).
 * Exposes window.BWMdfPricePoint { LADDER, BOARD_FLOORS, roundToLadder, boardFloorPence,
 *                                  priceOrder, variantKey, loadVariantMap }
 *
 * Each builder keeps its own cost formula (whole boards, or area units, plus the
 * per-panel cut fee) and hands the result here. This file owns the two things the
 * three builders must agree on: the per-board price floor and the ladder rounding.
 * scripts/build-mdf-csv.mjs loads this file to derive the ladder, so the Shopify
 * price-point products cannot drift from what the builders ask for.
 *
 * All money is ex VAT.
 */
(function () {
  'use strict';

  /* £5 rungs the whole way. Over LADDER.max = trade quote, same as the compact builders. */
  var LADDER = { min: 50, max: 5000, step: 5 };

  /* Per-board floor, pounds. `first` is the minimum charge for an order that uses one
     board; `extra` is added to that minimum for every further board the order runs into.
     A thickness belongs to the first band whose maxThicknessMm it does not exceed. */
  var BOARD_FLOORS = [
    { maxThicknessMm: 18,       first: 100, extra: 50 },
    { maxThicknessMm: Infinity, first: 120, extra: 90 }
  ];

  function roundToLadder(pounds) {
    return Math.round(pounds / LADDER.step) * LADDER.step;
  }

  function boardFloorPence(thicknessMm, boards) {
    var t = Number(thicknessMm) || 0;
    var n = Math.max(0, Math.floor(Number(boards) || 0));
    if (n === 0) return 0;
    var band = BOARD_FLOORS[BOARD_FLOORS.length - 1];
    for (var i = 0; i < BOARD_FLOORS.length; i++) {
      if (t <= BOARD_FLOORS[i].maxThicknessMm) { band = BOARD_FLOORS[i]; break; }
    }
    return (band.first + band.extra * (n - 1)) * 100;
  }

  function variantKey(prefix, pounds) {
    return 'BD_' + String(prefix || '').toUpperCase() + '_' + pounds + '_CP';
  }

  /**
   * opts: { costPence, thicknessMm, boards, mapPrefix, variantMap }
   * costPence is the builder's own price for the cut list, before the floor.
   */
  function priceOrder(opts) {
    var costPence = Math.max(0, Number(opts.costPence) || 0);
    var floorPence = boardFloorPence(opts.thicknessMm, opts.boards);
    var exactTotalPence = Math.max(costPence, floorPence);
    var roundedPounds = roundToLadder(exactTotalPence / 100);
    /* Rounding to the nearest rung must never take the price back under the floor. */
    if (roundedPounds * 100 < floorPence) roundedPounds += LADDER.step;

    var overMax = exactTotalPence > LADDER.max * 100;
    var pricePoint = Math.max(LADDER.min, Math.min(LADDER.max, roundedPounds));
    var key = variantKey(opts.mapPrefix, pricePoint);
    var variantMap = opts.variantMap || {};
    var varId = variantMap[key];

    return {
      costPence: costPence,
      floorPence: floorPence,
      floorApplied: floorPence > costPence,
      exactTotalPence: exactTotalPence,
      pricePoint: pricePoint,
      totalCost: pricePoint * 100,
      overMax: overMax,
      mapLoaded: Object.keys(variantMap).length > 0,
      resolvedVariant: (!overMax && varId) ? { id: varId, price: pricePoint * 100, configKey: key } : null
    };
  }

  async function loadVariantMap(url) {
    if (!url) return {};
    var res = await fetch(url);
    if (!res.ok) throw new Error('Variant map fetch failed: ' + res.status);
    return await res.json();
  }

  window.BWMdfPricePoint = {
    LADDER: LADDER,
    BOARD_FLOORS: BOARD_FLOORS,
    roundToLadder: roundToLadder,
    boardFloorPence: boardFloorPence,
    priceOrder: priceOrder,
    variantKey: variantKey,
    loadVariantMap: loadVariantMap
  };
})();
